<script setup>
import { Gradient } from "@/assets/js/Gradient";
import { useMainStore } from "@/store/MainStore";
import { ref, computed, onBeforeMount, onBeforeUnmount, onMounted } from "vue";
import logo from "@/assets/img/calid_logo_small.png";
import { useAuthStore } from "@/store/AuthStore";
import router from "@/router";
import ("@/assets/css/auth.css");
import ProgressSpinner from "primevue/progressspinner";
import MFAChallenge from "@/components/Auth/MFAChallenge.vue";
import { useMFA } from "@/composables/useMFA";
import { useWebAuthn } from "@/composables/useWebAuthn";

// Initialise
const gradient = new Gradient();
const store = useMainStore();
const authStore = useAuthStore();
const mfa = useMFA();
const webauthn = useWebAuthn();
const body = document.getElementsByTagName("body")[0];

const isLoading = ref(false);
const username = ref('');
const password = ref('');
const errorMessage = ref('');
const missingFields = ref([]);

// MFA state
const showMFAChallenge = ref(false);
const mfaChallengeToken = ref('');
const mfaAvailableMethods = ref(['totp']);

// Check if fields have values
const hasUsername = computed(() => username.value.trim() !== "");
const hasPassword = computed(() => password.value.trim() !== "");

// Validation Function
const validateLogin = () => {
  if (!hasUsername.value || username.value.indexOf('@') === -1) {
    errorMessage.value = "Please enter a valid email.";
    return false;
  }
  if (!hasPassword.value) {
    errorMessage.value = "Please enter your password.";
    return false;
  }
  errorMessage.value = ""; // Clear any previous errors
  return true;
};

// Login Handler
const handleLogin = async () => {
  if (!validateLogin()) {
    return;
  }
  isLoading.value = true;
  const API_URL = import.meta.env.VITE_API_URL;
  console.log('API URL:', API_URL);

  try {
    const response = await fetch(API_URL + '/auth/token/', {
      method: 'POST',
      body: JSON.stringify({
        email: username.value,
        password: password.value,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Check if response has content before parsing
    const text = await response.text();
    let data = {};
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('Failed to parse response:', text);
        errorMessage.value = "Server returned an invalid response. Please try again.";
        return;
      }
    }

    // Handle different response scenarios
    if (!response.ok) {
      errorMessage.value = data.message || data.detail || `Login failed (${response.status})`;
    } else if (data.requires_2fa) {
      // 2FA required - show challenge
      mfaChallengeToken.value = data.challenge_token;
      mfaAvailableMethods.value = data.available_methods || ['totp'];
      showMFAChallenge.value = true;
    } else if (data.missing && Array.isArray(data.missing) && data.missing.length) {
      missingFields.value = data.missing;
      errorMessage.value = "We need a few details to finish setting up your account.";
    } else if (response.status === 200 && data.token) {
      // Successful login (no 2FA required)
      completeLogin(data.token, data.refresh_token);
    } else {
      // Handle error response
      errorMessage.value = data.message || "Login failed: Check your email and password.";
    }
  } catch (error) {
    errorMessage.value = "An error occurred while trying to log in. Please try again.";
    console.error('Login error:', error);
  } finally {
    isLoading.value = false;
  }
};

// Complete login after successful auth or 2FA
const completeLogin = (token, refreshToken) => {
  authStore.login(token, refreshToken);
  setTimeout(() => {
    router.push('/dashboard');
  }, 500);
};

// Handle MFA success
const onMFASuccess = (response) => {
  showMFAChallenge.value = false;
  completeLogin(response.token, response.refresh_token);
};

// Handle MFA cancel
const onMFACancel = () => {
  showMFAChallenge.value = false;
  mfaChallengeToken.value = '';
  password.value = '';
};

// Passkey login (passwordless)
const handlePasskeyLogin = async () => {
  if (!webauthn.isSupported.value) return;

  isLoading.value = true;
  errorMessage.value = '';

  try {
    // Get options with challengeToken
    const { options, challengeToken } = await mfa.getPasskeyLoginOptions(username.value || null);
    
    // Check if user has passkeys registered
    if (!options || (Array.isArray(options) && options.length === 0) || !options.challenge) {
      throw new Error('No passkeys registered. Please sign in with your password first and set up a passkey.');
    }
    
    const credential = await webauthn.authenticatePasskey(options);
    const response = await mfa.verifyPasskeyLogin(credential, challengeToken);

    if (response.token) {
      completeLogin(response.token, response.refresh_token);
    }
  } catch (error) {
    if (error.name === 'NotAllowedError') {
      errorMessage.value = 'Passkey authentication was cancelled';
    } else {
      errorMessage.value = error.message || 'Passkey login failed';
    }
  } finally {
    isLoading.value = false;
  }
};

// Lc hooks
onBeforeMount(() => {
  store.layout = "auth";
  store.hideConfigButton = true;
  store.showNavbar = false;
  store.showSidenav = false;
  store.showFooter = false;
  body.classList.remove("bg-gray-100");
});
onBeforeUnmount(() => {
  const link = document.querySelector('link[href*="auth.css"]');
  if (link) {
    link.remove();
  }
  store.layout = "default";
  store.hideConfigButton = false;
  store.showNavbar = true;
  store.showSidenav = true;
  body.classList.add("bg-gray-100");
});
onMounted(() => {
  gradient.initGradient('#gradient-canvas');

  // Toggle password visibility
  const button = document.querySelector("#show-pass");
  const icon = document.querySelector("#show-pass-icon");
  const textarea = document.querySelector("#password");

  if (button && icon && textarea) {
    button.addEventListener("click", () => {
      const type = textarea.getAttribute("type") === "password" ? "text" : "password";
      textarea.setAttribute("type", type);
      icon.classList.toggle("fa-eye");
      icon.classList.toggle("fa-eye-slash");
    });
  }
});
</script>

<template>
  <div class="container-fluid" id="header-gradient">
    <canvas class="rounded-2" id="gradient-canvas"></canvas>
  </div>
  <div id="auth-container">
    <!-- MFA Challenge Screen -->
    <transition name="fade" mode="out-in">
      <MFAChallenge
        v-if="showMFAChallenge"
        :challenge-token="mfaChallengeToken"
        :available-methods="mfaAvailableMethods"
        :email="username"
        @success="onMFASuccess"
        @cancel="onMFACancel"
      />

      <!-- Login Form -->
      <form v-else class="login-form validate-form" @submit.prevent="handleLogin">
        <span class="title pb-3">
          <img class="auth-logo" :src="logo" alt="Calid Logo"/>
        </span>
        <span class="title mb-5">Welcome to Calid</span>

        <!-- Error Message -->
        <p class="error-message" v-if="errorMessage">{{ errorMessage }}</p>
        <div v-if="missingFields.length" class="alert alert-warning missing-alert">
          <div class="d-flex align-items-center gap-2">
            <i class="fas fa-info-circle"></i>
            <div>
              <div class="fw-semibold">Finish setup</div>
              <div class="small">Please provide: {{ missingFields.join(', ') }}</div>
            </div>
          </div>
          <router-link to="/account/get-started" class="alert-link">Complete now</router-link>
        </div>

        <div
            class="wrapper-input validate-input"
            :class="{ 'input-error': errorMessage && !hasUsername }"
            data-validate="Example: john@doe.com"
        >
          <input
              class="input pass-font"
              type="text"
              name="username"
              id="username"
              v-model="username"
              required
              autocomplete="username webauthn"
              :class="{ 'has-val': hasUsername }"
          />
          <span
              class="focus-input"
              data-placeholder="Email"
          ></span>
        </div>

        <div
            class="wrapper-input validate-input"
            :class="{ 'input-error': errorMessage && !hasPassword }"
            data-validate="Enter password"
            style="margin-bottom: 0px; !important"
        >
          <span class="show-pw" id="show-pass">
            <i class="fa fa-eye" id="show-pass-icon"></i>
          </span>
          <input
              class="input pass-font"
              type="password"
              name="password"
              id="password"
              v-model="password"
              required
              autocomplete="current-password"
              :class="{ 'has-val': hasPassword }"
          />
          <span
              class="focus-input"
              data-placeholder="Password"
          ></span>
        </div>

        <div class="container-btn mt-3">
          <div class="wrap-btn">
            <div class="bg-btn"></div>
            <button class="form-btn" type="submit">
              Login
            </button>
          </div>
        </div>

        <!-- Passkey Login Button -->
        <div v-if="webauthn.isSupported.value" class="passkey-login-section">
          <div class="divider">
            <span>or</span>
          </div>
          <button type="button" class="passkey-btn" @click="handlePasskeyLogin" :disabled="isLoading">
            <i class="fas fa-fingerprint"></i>
            Sign in with Passkey
          </button>
        </div>

        <hr/>

        <div class="text-center mt-3">
          <span class="text-1">Not got an account?</span>
          <a class="text-2 px-1" href="/signup">Sign Up</a>
          <br/>
          <span class="text-1">Forgot your password?</span>
          <a class="text-2 px-1" href="/reset-password">Reset it</a>
        </div>
        <footer class="text-center mt-2">
          <small class="text-small text-muted">&copy; 2024 Zach Matcham</small>
        </footer>
        <div v-if="isLoading" class="loading-overlay">
          <ProgressSpinner />
        </div>
      </form>
    </transition>
  </div>
</template>

<style scoped>
.error-message {
  color: red;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.missing-alert {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.missing-alert .alert-link {
  color: #9a3412;
  font-weight: 600;
  text-decoration: underline;
}

.input-error .input {
  border-color: red;
}
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Passkey Login Styles */
.passkey-login-section {
  margin-top: 1.5rem;
}

.divider {
  display: flex;
  align-items: center;
  margin: 1rem 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

.divider span {
  padding: 0 1rem;
  color: #9ca3af;
  font-size: 0.875rem;
}

.passkey-btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: white;
  color: #374151;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.passkey-btn:hover:not(:disabled) {
  border-color: #667eea;
  color: #667eea;
  background: #f5f3ff;
}

.passkey-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.passkey-btn i {
  font-size: 1.25rem;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
